import styles from "./Annotation.module.scss";
import { Typography } from "@/frontend/shared/ui/Typography";
import { UsersAvatar } from "@/frontend/shared/ui/UsersAvatar/UsersAvatar";
import { TGetAnnotationsData } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/model/types/types";
import { CaretDownIcon, DotsThreeIcon, EditIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { NormalButton, SquareButton } from "@/frontend/shared/ui/Button";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { Fragment, useState } from "react";
import { NewAnnotation } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/ui/NewAnnotation/NewAnnotation";
import { Divider } from "@/frontend/shared/ui/Divider/Divider";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { getHowLongTimeString } from "@/frontend/shared/lib/getHowLongTimeString/getHowLongTimeString";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";

export function Annotation({
  annotation,
  companyId,
  onDelete,
  canEdit,
  canDelete,
  flat
}: {
  annotation: TGetAnnotationsData;
  companyId: number;
  onDelete: (id: number) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  flat?: boolean;
}) {
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showReplies, setShowReplies] = useState(false);

  const [repliesAmmount, setRepliesAmmount] = useState(5);

  const { data: meData } = useMe();

  const onEdit = () => {
    setShowActionsMenu(false);
    setIsEditing(true);
  };

  const onDeleteAnnotation = (id: number) => {
    setShowActionsMenu(false);
    onDelete(id);
  };

  const isMobile = useScreenWidth() <= 768;

  return (
    <div className={flat ? styles.flat : styles.card}>
      {isEditing && (
        <NewAnnotation
          companyId={companyId}
          onCancel={() => {
            setIsEditing(false);
          }}
          onSuccess={() => {
            setIsEditing(false);
          }}
          content={annotation.content}
          patch
          annotationId={annotation.id}
          flat
          organizationId={annotation.organizationId}
        />
      )}
      {!isEditing && (
        <div className={styles.annotationContentContainer}>
          {!isMobile && (
            <div>
              <UsersAvatar
                nameInitials={annotation.createdBy.firstName[0] + annotation.createdBy.lastName[0]}
                profileImg={annotation.createdBy.profileImageUrl}
              />
            </div>
          )}

          <div className={styles.content}>
            <div className={styles.infoSection}>
              {isMobile && (
                <div>
                  <UsersAvatar
                    nameInitials={annotation.createdBy.firstName[0] + annotation.createdBy.lastName[0]}
                    profileImg={annotation.createdBy.profileImageUrl}
                    className={styles.smallAvatar}
                  />
                </div>
              )}
              <Typography variant="label-4" color="greyscale900">
                {annotation.createdBy?.firstName} {annotation.createdBy?.lastName}
              </Typography>
              <time dateTime={new Date(annotation.updatedAt).toDateString()}>
                <Typography variant="body-2" color="greyscale500" style={{ lineHeight: "14px" }}>
                  {getHowLongTimeString(new Date(annotation.updatedAt))}
                </Typography>
              </time>

              <div className={styles.actionsContainer}>
                <SquareButton
                  size="small"
                  variant="tertiary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowActionsMenu(true);
                  }}
                >
                  <DotsThreeIcon className={styles.threeDotsIcon} />
                </SquareButton>
                <DropshadowMenu
                  isOpen={showActionsMenu}
                  onClose={() => setShowActionsMenu(false)}
                  className={styles.actionsMenu}
                  options={[
                    <button className={styles.action} onClick={onEdit} disabled={!canEdit}>
                      <EditIcon />
                      <Typography variant="label-4" color="greyscale800">
                        Edit
                      </Typography>
                    </button>,
                    <button
                      className={styles.action}
                      onClick={() => onDeleteAnnotation(annotation.id)}
                      disabled={!canDelete}
                    >
                      <TrashIcon />
                      <Typography variant="label-4" color="greyscale800">
                        Delete
                      </Typography>
                    </button>
                  ]}
                  top="-10px"
                  right="0px"
                  dividerIndexRate={1}
                />
              </div>
            </div>
            <div className={styles.annotation}>
              <Typography variant="body-2" style={{ whiteSpace: "pre-wrap" }} color="greyscale800">
                {annotation.content}
              </Typography>
            </div>
            <div className={styles.repliesControls}>
              {!flat && (
                <NormalButton variant="tertiary" size="medium" onClick={() => setShowReplies((v) => !v)}>
                  {annotation.replies.length > 0
                    ? annotation.replies.length == 1
                      ? `${annotation.replies.length} Reply`
                      : `${annotation.replies.length} Replies`
                    : "Reply"}{" "}
                  <CaretDownIcon className={showReplies ? styles.caretDownIconExpanded : styles.caretDownIcon} />
                </NormalButton>
              )}
            </div>
            {showReplies && <Divider />}
          </div>
        </div>
      )}

      {showReplies && (
        <div className={styles.replies}>
          <NewAnnotation
            companyId={companyId}
            compact
            flat
            replyToId={annotation.id}
            organizationId={annotation.organizationId}
            textAreaPlaceholder="Reply..."
          />
          {annotation.replies?.length > 0 ? <Divider /> : null}

          {showReplies &&
            annotation.replies
              ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .slice(0, repliesAmmount)
              .map((reply, index) => {
                const canModify =
                  reply.createdBy.id === meData?.id ||
                  (meData?.entity === "Investor" && reply.createdBy.entity === "Expert");
                return (
                  <Fragment key={reply.id}>
                    <Annotation
                      annotation={reply}
                      companyId={companyId}
                      onDelete={onDeleteAnnotation}
                      canDelete={canModify}
                      canEdit={canModify}
                      flat
                    />
                    {index !== annotation.replies.length - 1 && <Divider />}
                  </Fragment>
                );
              })}
          {annotation.replies?.length > repliesAmmount && (
            <div>
              <NormalButton size="medium" variant="tertiary" onClick={() => setRepliesAmmount((v) => v + 5)}>
                View more replies ({annotation.replies.length - repliesAmmount}){" "}
                <CaretDownIcon className={styles.caretDownIcon} />
              </NormalButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
